import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

type Props = {
    to: string;
    eyebrow?: string;
    title: string;
    body?: string;
    cta?: string;
    /** Optional photographic hero. Takes precedence over `icon`. */
    image?: { src: string; alt?: string };
    /** Optional topical icon shown over a coloured gradient. Decorative. */
    icon?: ReactNode;
    /** Used as the gradient base when `icon` is set. Defaults to --gt-primary. */
    accentColor?: string;
};

// Clickable card linking into a guide (or any internal route). When an
// image or icon is supplied the card renders an Explore-style visual
// header above the body; without either, it's a text-only card (used by
// the Landing "Heading abroad?" callout).
export default function GuideCard({
    to,
    eyebrow,
    title,
    body,
    cta,
    image,
    icon,
    accentColor,
}: Props) {
    const hasVisual = !!image || !!icon;
    const accentStyle = accentColor
        ? ({ "--gt-guide-accent": accentColor } as CSSProperties)
        : undefined;

    return (
        <Link
            to={to}
            className={`gt-card gt-card--clickable gt-guide-card${
                hasVisual ? " gt-guide-card--visual" : ""
            }`}
            style={accentStyle}
        >
            {image ? (
                <img
                    src={image.src}
                    alt={image.alt || ""}
                    loading="lazy"
                    className="gt-guide-card__visual gt-guide-card__visual--image"
                />
            ) : icon ? (
                <div
                    className="gt-guide-card__visual gt-guide-card__visual--icon"
                    aria-hidden="true"
                >
                    {icon}
                </div>
            ) : null}
            <div className="gt-guide-card__body">
                {eyebrow && <div className="gt-eyebrow">{eyebrow}</div>}
                <h3 className="gt-h3 gt-guide-card__title">{title}</h3>
                {body && <p className="gt-guide-card__body-text">{body}</p>}
                {cta && <span className="gt-guide-card__cta">{cta} →</span>}
            </div>
        </Link>
    );
}
